import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DmInputTextLibComponent } from '@everymatrix-base-components/dm-input-text-lib';
import { DmButtonLibComponent } from '@everymatrix-base-components/dm-button-lib';
import {
  GlobalMessageService,
  ReportTemplate,
  TemplateService,
} from '@everymatrix-base-components/utils';
import { ProgressSpinner } from 'primeng/progressspinner';
import { take } from 'rxjs';
import { RadioButtonModule } from 'primeng/radiobutton';

@Component({
  selector: 'lib-save-template-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    DmInputTextLibComponent,
    DmButtonLibComponent,
    ReactiveFormsModule,
    ProgressSpinner,
    RadioButtonModule,
  ],
  template: `
    <p-dialog
      header="Save Template"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '460px' }"
      (onShow)="onShowDialog()"
    >
      @if(isTemplatePresent) {
      <div class="radio-buttons-container">
        <div class="radio-button">
          <p-radiobutton
            value="existing"
            inputId="existing"
            [formControl]="radioButtonControl"
          />
          <label for="existing" class="radio-button-label"
            >Save Changes To Existing Template</label
          >
        </div>
        <div class="radio-button">
          <p-radiobutton
            value="new"
            inputId="new"
            [formControl]="radioButtonControl"
          />
          <label for="new" class="radio-button-label"
            >Save As New Template</label
          >
        </div>
      </div>
      }
      <div class="fields-wrapper">
        <div class="field" [ngStyle]="{ 'margin-bottom': '12px' }">
          <lib-dm-input-text-lib
            label="Name"
            placeholder="name"
            [errorMessage]="hintMessage"
            [hint]="hintMessage"
            [control]="nameControl"
          />
        </div>
        <div class="field">
          <lib-dm-input-text-lib
            label="Description"
            placeholder="Description"
            [control]="descriptionControl"
          />
        </div>
      </div>
      <ng-template pTemplate="footer">
        <div
          style="width: 100%; display: flex; flex-direction: row; justify-content: space-between"
        >
          <lib-dm-button-lib
            text="Cancel"
            size="medium"
            variant="secondary-outlined"
            (outsideClickEmitter)="close()"
          />
          <lib-dm-button-lib
            text="Save"
            size="medium"
            variant="primary-filled"
            [disabled]="nameControl.invalid || descriptionControl.invalid"
            (outsideClickEmitter)="save()"
          >
            @if(loading()) {
            <div class="dm-button-icon" icon-left>
              <p-progress-spinner
                strokeWidth="6"
                [style]="{ width: '20px', height: '20px' }"
              />
            </div>
            }
          </lib-dm-button-lib>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styleUrls: ['./save-template-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveTemplateModalComponent {
  visible = model(false);
  params = input.required();
  reportId = input<number | undefined>();
  loading = signal(false);
  saveEmitter = output();
  isTemplatePresent = false;
  isExisting = false;
  template: ReportTemplate | null | undefined;
  hintMessage =
    'Available symbols for template name: letters, numbers, underscores, dashes';

  templateService = inject(TemplateService);
  globalMessageService = inject(GlobalMessageService);

  nameControl = new FormControl('', [
    Validators.required,
    Validators.pattern(/^[A-Za-z0-9\-_]+$/),
  ]);
  descriptionControl = new FormControl('', [Validators.maxLength(255)]);
  radioButtonControl = new FormControl('existing');

  constructor() {
    effect(() => {
      this.template = this.templateService.template();
      if (this.template && !this.template.is_temporary) {
        this.isTemplatePresent = true;
        this.descriptionControl.setValue(this.template.template_desc);
        this.nameControl.setValue(this.template.template_name);
      } else {
        this.isTemplatePresent = false;
        this.isExisting = false;
        this.descriptionControl.reset();
        this.nameControl.reset();
      }
    });
    this.radioButtonControl.valueChanges.subscribe((value) => {
      if (value === 'existing' && this.template) {
        this.isExisting = true;
        this.descriptionControl.setValue(this.template.template_desc);
        this.nameControl.setValue(this.template.template_name);
      } else {
        this.isExisting = false;
        this.descriptionControl.reset('');
        this.nameControl.reset('');
      }
    });
  }

  onShowDialog() {
    if (this.isTemplatePresent) {
      this.radioButtonControl.setValue('existing');
    }
  }

  protected close(): void {
    this.visible.set(false);
    if (!this.isExisting) {
      this.nameControl.reset();
      this.descriptionControl.reset();
    }
  }

  protected save(): void {
    this.loading.set(true);
    this.templateService
      .saveTemplate(
        this.nameControl.value as string,
        this.descriptionControl.value,
        this.params(),
        this.reportId(),
        this.radioButtonControl.value === 'existing'
          ? this.template?.template_id
          : null,
        false
      )
      .pipe(take(1))
      .subscribe({
        next: (template: any) => {
          this.radioButtonControl.setValue('existing');
          if (this.template?.template_id !== template.template_id) {
            this.templateService.setUrlTemplateId(template.template_id);
            this.templateService.templateChangedSubject.next(template);
          }
          this.updateTemplateList(template);
          this.templateService.template.set(template);
          this.globalMessageService.showSuccessMessage({
            detail: 'The Report has been saved successfully',
          });
          this.loading.set(false);
          this.saveEmitter.emit();
          this.close();
        },
        error: (err) => {
          this.globalMessageService.showErrorMessage({
            detail: err,
          });
          this.loading.set(false);
        },
      });
  }

  private updateTemplateList(template: ReportTemplate) {
    this.templateService.templatesList.update((arr) => {
      const list = [...(arr as ReportTemplate[])];
      const index = list.findIndex(
        (t) => t.template_id === template.template_id
      );

      if (index !== -1) {
        list[index] = template;
      } else {
        list.push(template);
      }

      return list;
    });
  }
}
